import React, { useEffect, useMemo, useState } from "react";
import api from "../../chat/api";
import Shell from "./CopilotCardShell";

/* The outline, before anything is written.
 *
 * The one moment to change the proposal's shape while changing it is still
 * free: every section written is minutes of model time, so a section removed
 * here is the cheapest edit in the flow.
 *
 * REMOVAL, NOT TICKING. A quiet × on the row rather than a column of
 * checkboxes, matching the reference build — and it is the better read. A
 * checkbox list says "choose which of these you want" when the honest framing
 * is "this IS the proposal; take out anything that does not belong". It also
 * frees the left column for the numbering, which is what makes the tree
 * scannable at a glance.
 *
 * Removed rows stay, struck through, with the × becoming a restore. Vanishing
 * on click makes a misclick unrecoverable without rebuilding the whole outline.
 *
 * ON THE COUNTS. The reference shows "26 requirements" and "4 reqs" per
 * section. Sections in this codebase carry no requirement linkage — the stored
 * TOC is { id, number, title, children } and nothing else — so those numbers
 * cannot be derived, only invented. Subsection counts are real, and are shown
 * instead. The same missing mapping is what Phase 7's derived-coverage rule
 * will need.
 */

const ProposalOutlineCard = ({ message, onConfirm }) => {
  const { projectId, projectName } = message;

  const [toc, setToc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [outSections, setOutSections] = useState(() => new Set());
  const [outSubs, setOutSubs] = useState(() => new Set());
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        /* Not generate-proposal/: that returns `proposal_content`, whose
         * clean_node strips ids and children — a flat list of titles that
         * cannot build a tree. And not section-view/, which wraps the same TOC
         * in members, comments and review state. This GET returns the outline
         * and nothing else. */
        const res = await api.get(`/api/v1/rfp/folders/${projectId}/proposal/outline/confirm/`);
        if (!alive) return;
        setToc(Array.isArray(res?.data?.toc) ? res.data.toc : []);
      } catch {
        if (alive) setError("Couldn't load the outline.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [projectId]);

  const toggle = (set, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const counts = useMemo(() => {
    let sections = 0;
    let subs = 0;
    for (const s of toc || []) {
      if (outSections.has(s.id)) continue;
      sections += 1;
      for (const c of s.children || []) if (!outSubs.has(c.id)) subs += 1;
    }
    return { sections, subs };
  }, [toc, outSections, outSubs]);

  const confirm = async () => {
    setConfirming(true);
    setError(null);
    try {
      const res = await api.post(`/api/v1/rfp/folders/${projectId}/proposal/outline/confirm/`, {
        excluded_section_ids: [...outSections],
        // A subsection inside a removed section goes with its parent; sending it
        // too would ask to remove something already gone.
        excluded_subsection_ids: [...outSubs].filter((id) =>
          (toc || []).some(
            (s) => !outSections.has(s.id) && (s.children || []).some((c) => c.id === id)
          )
        ),
      });

      const failed = res?.data?.failed || [];
      if (failed.length) {
        setError(
          `${failed.length} of those couldn't be removed. The outline below is what actually saved.`
        );
        setToc(res?.data?.toc || toc);
        return;
      }

      setDone(true);
      onConfirm?.({
        projectId,
        projectName,
        sections: res?.data?.sections ?? 0,
        subsections: res?.data?.subsections ?? 0,
      });
    } catch {
      setError("Couldn't apply those changes to the outline.");
    } finally {
      setConfirming(false);
    }
  };

  const nothingLeft = !loading && counts.sections === 0;

  /* The × / restore control. Always in the DOM so rows never reflow on hover,
   * revealed on hover AND on keyboard focus — a control that exists only under
   * a pointer does not exist for a keyboard at all. */
  const RowAction = ({ removed, onClick, label }) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`flex h-5 w-5 flex-none items-center justify-center rounded-md text-[#77707E] transition-[opacity,color,background-color] duration-150 hover:bg-[#18101C]/[0.06] hover:text-[#18101C] focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none ${
        removed ? "opacity-100" : "opacity-0 group-hover/row:opacity-100"
      }`}
    >
      {removed ? (
        <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
          <path d="M3 3v5h5" />
        </svg>
      ) : (
        <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      )}
    </button>
  );

  return (
    <Shell
      icon={
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          <polyline points="14 3 14 8 19 8" />
          <path d="M9 13h6M9 16.5h4" />
        </svg>
      }
      title="Proposal outline"
      width={640}
      meta={
        !loading && !error ? (
          <span className="text-[12px] tabular-nums text-[#6B6472]">
            {counts.sections} section{counts.sections === 1 ? "" : "s"} · {counts.subs} subsection
            {counts.subs === 1 ? "" : "s"}
          </span>
        ) : null
      }
      footer={
        <>
          <button
            type="button"
            onClick={confirm}
            disabled={loading || confirming || done || nothingLeft}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-[#A64E9D] to-[#8E4187] px-4 py-2 text-[13.5px] font-semibold text-white ring-1 ring-inset ring-white/[0.16] shadow-[0_1px_2px_rgba(24,16,28,0.10),0_8px_18px_-10px_rgba(142,65,135,0.65)] transition-[transform,box-shadow,filter] duration-200 hover:-translate-y-[1px] hover:brightness-[1.04] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2 motion-reduce:transition-none"
          >
            {confirming && (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-[2px] border-white/40 border-t-white motion-reduce:animate-none" />
            )}
            {done ? "Approved" : confirming ? "Applying…" : "Looks right — continue"}
          </button>

          <span className="ml-auto text-[12px] text-[#6B6472]">
            {nothingLeft ? (
              <span className="text-[#92620B]">Keep at least one section.</span>
            ) : (
              "Nothing is written until you continue"
            )}
          </span>
        </>
      }
    >
      {loading ? (
        <div className="space-y-2 px-4 py-3.5" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-6 animate-pulse rounded bg-[#18101C]/[0.05]" />
          ))}
        </div>
      ) : error ? (
        <p role="alert" className="px-4 py-3.5 text-[13px] text-[#B91C1C]">
          {error}
        </p>
      ) : (
        // 68vh, taller than the other cards on purpose: this one is READ, not
        // glanced at. A 51-row tree in a 46vh window showed four sections, so
        // judging the shape of the proposal became a scrolling exercise.
        <ul className="max-h-[68vh] overflow-y-auto sensa-scroll py-2">
          {(toc || []).map((s) => {
            const gone = outSections.has(s.id);
            const kept = (s.children || []).filter((c) => !outSubs.has(c.id)).length;
            return (
              <li key={s.id} className="mb-1 last:mb-0">
                <div
                  className={`group/row flex items-center gap-3 px-4 py-1.5 transition-colors hover:bg-[#18101C]/[0.025] motion-reduce:transition-none ${
                    gone ? "opacity-45" : ""
                  }`}
                >
                  <span className="w-6 flex-none text-[13px] font-medium tabular-nums text-[#A04A97]">
                    {String(s.number || "").replace(/\.0$/, "")}
                  </span>
                  <span
                    className={`min-w-0 flex-1 text-[14.5px] font-semibold leading-snug text-[#18101C] ${
                      gone ? "line-through" : ""
                    }`}
                  >
                    {s.title}
                  </span>
                  {!gone && !!(s.children || []).length && (
                    <span className="flex-none text-[12px] tabular-nums text-[#77707E]">
                      {kept} sub{kept === 1 ? "" : "s"}
                    </span>
                  )}
                  <RowAction
                    removed={gone}
                    onClick={() => setOutSections((p) => toggle(p, s.id))}
                    label={gone ? `Put back: ${s.title}` : `Remove section: ${s.title}`}
                  />
                </div>

                {/* A rule down the left of the children, so a subsection reads
                    as belonging to the section above it rather than as another
                    row in one long list. It starts and stops with the group —
                    a line running past the last child would imply more.

                    ml-[27px] puts it under the centre of the section's number,
                    and the 12px padding inside restores the 40px text indent
                    the rows had before, so nothing shifts. */}
                {!!(s.children || []).length && (
                  <div className={`ml-[27px] border-l border-[#18101C]/[0.09] ${gone ? "opacity-45" : ""}`}>
                {(s.children || []).map((c) => {
                  const subGone = gone || outSubs.has(c.id);
                  return (
                    <div
                      key={c.id}
                      className={`group/row flex items-center gap-3 py-[5px] pl-3 pr-4 transition-colors hover:bg-[#18101C]/[0.025] motion-reduce:transition-none ${
                        !gone && subGone ? "opacity-45" : ""
                      }`}
                    >
                      <span className="w-8 flex-none text-[12.5px] tabular-nums text-[#A04A97]/75">
                        {c.number}
                      </span>
                      <span
                        className={`min-w-0 flex-1 text-[13.5px] leading-snug text-[#3F3F46] ${
                          subGone ? "line-through" : ""
                        }`}
                      >
                        {c.title}
                      </span>
                      {/* A subsection of a removed section is not a decision the
                          user still has — its control goes with its parent. */}
                      {!gone && (
                        <RowAction
                          removed={outSubs.has(c.id)}
                          onClick={() => setOutSubs((p) => toggle(p, c.id))}
                          label={
                            outSubs.has(c.id)
                              ? `Put back: ${c.title}`
                              : `Remove subsection: ${c.title}`
                          }
                        />
                      )}
                    </div>
                  );
                })}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Shell>
  );
};

export default ProposalOutlineCard;
